/**
 * Traverses the provided root object depth first
 * @param nodeOrNodes The object to traverse
 * @param children Function providing the children of the object
 * @param callback Function called on each traversed node
 */
export function traverse(nodeOrNodes, children, callback) {
    if (Array.isArray(nodeOrNodes)) {
        return nodeOrNodes.forEach(node => {
            traverseInternal(node, children, callback, {
                node,
                root: node,
                siblings: [],
                index: 0,
                depth: 0,
            });
        });
    }
    return traverseInternal(nodeOrNodes, children, callback, {
        node: nodeOrNodes,
        root: nodeOrNodes,
        siblings: [],
        index: 0,
        depth: 0,
    });
}
function traverseInternal(node, children, callback, context) {
    callback(node, context);
    const nodes = children(node);
    for (let index = 0; index < nodes.length; index++) {
        traverseInternal(nodes[index], children, callback, {
            ...context,
            parent: {
                ...context,
                node,
            },
            siblings: nodes,
            node: nodes[index],
            index,
            depth: context.depth + 1,
        });
    }
}
/**
 * Reduces the provided tree node to a value traversing depth first
 * @param nodeOrNodes The node or nodes to traverse
 * @param children Function providing the children of the object
 * @param callback Function called on each traversed node
 * @param initialValue The initial value of the reduction
 */
export function reduce(nodeOrNodes, children, callback, initialValue) {
    const resultHolder = { result: initialValue };
    traverse(nodeOrNodes, children, (value, state) => {
        resultHolder.result = callback(resultHolder.result, value, state);
    });
    return resultHolder.result;
}
/**
 * Returns true if one of the tree nodes matches the provided matcher
 * @param nodeOrNodes The node or nodes to traverse
 * @param children Function providing the children of the object
 * @param matcher Function called on each node
 */
export function some(nodeOrNodes, children, matcher) {
    return reduce(nodeOrNodes, children, (result, value, context) => (result ? true : matcher(value, context)), false);
}
/**
 * Perform a depth-first-search on the given tree, returning the first node that satisfies the matcher.
 *
 * @param nodeOrNodes   The root node(s)
 * @param children      An operator that returns the child nodes for the given node
 * @param matcher       A test function that either accepts or rejects a node
 */
export function depthFirstSearch(nodeOrNodes, children, matcher) {
    const processQueue = Array.isArray(nodeOrNodes)
        ? nodeOrNodes.map((node, index) => ({
            node,
            index,
            root: node,
            siblings: nodeOrNodes,
            depth: 0,
        }))
        : [
            {
                root: nodeOrNodes,
                siblings: [nodeOrNodes],
                node: nodeOrNodes,
                index: 0,
                depth: 0,
            },
        ];
    while (processQueue.length > 0) {
        const current = processQueue.pop();
        if (matcher(current.node, current)) {
            return current.node;
        }
        const currentChildren = children(current.node);
        processQueue.push(...currentChildren.map((child, index) => ({
            ...current,
            index,
            parent: {
                ...current,
            },
            node: child,
            siblings: currentChildren,
            depth: current.depth + 1,
        })));
    }
    return undefined;
}
//# sourceMappingURL=arrayTrees.js.map